import { internalNetworkAssetsClient } from '@terra/shared-rest-clients-internal-network';
import type {
  InternalNetworkAssetListQueryDto,
  InternalNetworkAssetsContract,
} from '@terra/shared-contracts-internal-network';
import { tenantAwareApiWrapper, withOrchestratorClient } from './api-core';

export const internalNetworkAssetsApi = {
  listCanonicalInternalNetworkAssets: ({
    networkId,
    query,
  }: {
    networkId: string;
    query: InternalNetworkAssetListQueryDto;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkAssetsContract.listAssets>({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.listAssets({
          params: { networkId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),
  searchCanonicalInternalNetworkAssets: ({
    networkId,
    query,
  }: {
    networkId: string;
    query: InternalNetworkAssetListQueryDto & { search: string };
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkAssetsContract.searchAssets>({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.searchAssets({
          params: { networkId },
          query,
          ...withOrchestratorClient(headers),
        }),
    }),
  getCanonicalInternalNetworkAsset: ({
    networkId,
    assetId,
  }: {
    networkId: string;
    assetId: string;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkAssetsContract.getAsset>({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.getAsset({
          params: { networkId, assetId },
          ...withOrchestratorClient(headers),
        }),
    }),
  getCanonicalInternalNetworkAssetRelationships: ({
    networkId,
    assetId,
    limit = 50,
  }: {
    networkId: string;
    assetId: string;
    limit?: number;
  }) =>
    tenantAwareApiWrapper<
      typeof InternalNetworkAssetsContract.getAssetRelationships
    >({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.getAssetRelationships({
          params: { networkId, assetId },
          query: { limit },
          ...withOrchestratorClient(headers),
        }),
    }),
  mergeCanonicalInternalNetworkAssets: ({
    networkId,
    sourceAssetId,
    targetAssetId,
    reason,
  }: {
    networkId: string;
    sourceAssetId: string;
    targetAssetId: string;
    reason: string;
  }) =>
    tenantAwareApiWrapper<typeof InternalNetworkAssetsContract.mergeAssets>({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.mergeAssets({
          params: { networkId },
          body: { sourceAssetId, targetAssetId, reason },
          ...withOrchestratorClient(headers),
        }),
    }),
  reverseCanonicalInternalNetworkAssetMerge: ({
    networkId,
    mergeEventId,
    reason,
  }: {
    networkId: string;
    mergeEventId: string;
    reason: string;
  }) =>
    tenantAwareApiWrapper<
      typeof InternalNetworkAssetsContract.reverseAssetMerge
    >({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.reverseAssetMerge({
          params: { networkId, mergeEventId },
          body: { reason },
          ...withOrchestratorClient(headers),
        }),
    }),
  dismissCanonicalInternalNetworkResolutionIssue: ({
    networkId,
    issueId,
    reason,
  }: {
    networkId: string;
    issueId: string;
    reason: string;
  }) =>
    tenantAwareApiWrapper<
      typeof InternalNetworkAssetsContract.dismissResolutionIssue
    >({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.dismissResolutionIssue({
          params: { networkId, issueId },
          body: { reason },
          ...withOrchestratorClient(headers),
        }),
    }),
  keepCanonicalInternalNetworkAssetsSeparate: ({
    networkId,
    issueId,
    reason,
  }: {
    networkId: string;
    issueId: string;
    reason: string;
  }) =>
    tenantAwareApiWrapper<
      typeof InternalNetworkAssetsContract.keepAssetsSeparate
    >({
      endpointFn: (headers) =>
        internalNetworkAssetsClient.keepAssetsSeparate({
          params: { networkId, issueId },
          body: { reason },
          ...withOrchestratorClient(headers),
        }),
    }),
};
